import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { EditIcon } from './Icons';
import PriceRegisterList from './PriceRegisterList';
import ListingInfo from './ListingInfo';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

// Defined at module level so it keeps a stable identity across MapView renders
function MapEvents({ onChange }) {
  const map = useMapEvents({
    moveend: () => onChange(map.getBounds()),
    zoomend: () => onChange(map.getBounds())
  });
  return null;
}

// While a search is active, zoom to whatever is still shown
function FitToListings({ listings, active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    const points = listings
      .filter((l) => l.latitude != null && l.longitude != null)
      .map((l) => [l.latitude, l.longitude]);
    if (points.length) map.fitBounds(points, { padding: [40, 40], maxZoom: 17 });
  }, [listings, active, map]);
  return null;
}

// One marker with its popup. The price register is only fetched while the popup is open.
function ListingMarker({ listing }) {
  const [open, setOpen] = useState(false);
  const popupRef = useRef(null);
  // The popup grows once the price register has loaded, so ask Leaflet to re-measure and re-position it
  const refreshPopup = useCallback(() => popupRef.current && popupRef.current.update(), []);

  return (
    <Marker
      position={[listing.latitude, listing.longitude]}
      eventHandlers={{
        popupopen: (e) => {
          popupRef.current = e.popup;
          setOpen(true);
        },
        popupclose: () => setOpen(false)
      }}
    >
      <Popup>
        {listing.image_url && (
          <img
            src={listing.image_url}
            alt={listing.id}
            style={{ width: 'auto', height: '100px' }}
          />
        )}
        <div>
          <h3>
            <a href={listing.url} target="_blank" rel="noopener noreferrer">{listing.title}</a>
            <Link to={`/listings/${encodeURIComponent(listing.id)}/edit`} className="popup-edit-link" title="Edit" aria-label={`Edit ${listing.title}`}>
              <EditIcon />
            </Link>
          </h3>
          <ListingInfo listing={listing} />
          {open && listing.eircode && <PriceRegisterList eircode={listing.eircode} onLoaded={refreshPopup} />}
        </div>
      </Popup>
    </Marker>
  );
}

function MapView({ listings, onBoundsChange, fitToListings = false }) {
  // Default center for Dublin
  const center = [53.3498, -6.2603];
  const zoom = 12;
  const [bounds, setBounds] = useState(null);

  const handleBoundsChange = (newBounds) => {
    setBounds(newBounds);
    onBoundsChange && onBoundsChange(newBounds);
  };

  return (
    <div style={{ height: '100vh', width: '100%', position: 'relative' }}>
      {bounds && (
        <div style={{
          position: 'fixed',
          top: '50px',
          right: '10px',
          zIndex: 1000,
          padding: '5px 10px',
          background: '#333',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          NE: {bounds.getNorthEast().lat.toFixed(4)}, {bounds.getNorthEast().lng.toFixed(4)}<br />
          SW: {bounds.getSouthWest().lat.toFixed(4)}, {bounds.getSouthWest().lng.toFixed(4)}
        </div>
      )}
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {listings
          .filter((l) => l.latitude != null && l.longitude != null)
          .map((listing) => (
            <ListingMarker key={listing.id} listing={listing} />
          ))}
        <FitToListings listings={listings} active={fitToListings} />
        <MapEvents onChange={handleBoundsChange} />
      </MapContainer>
    </div>
  );
}

export default MapView;